/**
 * MF-CUSTOM (Empat): newsletter popup, rebuilt as a real modal dialog.
 *
 * The theme's stock newsletter popup used <newsletter-popup>, which extends the "PopIn" class -
 * a small, non-modal corner card by design (PopIn.shouldTrapFocus is hardcoded to `false`, since a
 * corner toast shouldn't block interaction with the rest of the page). The redesigned popup is a
 * full centered modal instead, so it needs real modal semantics: a dimmed backdrop, a focus trap,
 * Escape-to-close, and the page locked from scrolling behind it - exactly what the theme's existing
 * generic `Modal` class (registered as <x-modal>) already provides, but with none of the "wait N
 * seconds, then show once" behaviour that made the stock popup work as a popup in the first place.
 *
 * Rather than editing theme.js (assets/theme.js is vendored/regenerated by Shopify and gets
 * overwritten on a theme update - see CUSTOM-CHANGES.md), this file extends `Modal` from the
 * "theme" import map entry theme.liquid already exposes, and re-adds only the popup-specific
 * timing/localStorage logic, copied as-is from theme.js's NewsletterPopup class so behaviour and
 * storage keys stay consistent with the rest of the theme (e.g. the "only show once" state is
 * shared with the stock popup if it's ever re-enabled).
 *
 * See sections/newsletter-popup.liquid and CUSTOM-CHANGES.md for the full rationale.
 */
import { Modal } from 'theme';

class NewsletterModalPopup extends Modal {
  connectedCallback() {
    super.connectedCallback();

    if (this.shouldAppearAutomatically) {
      setTimeout(() => this.show(), this.apparitionDelay);
    }
  }

  get apparitionDelay() {
    return parseInt(this.getAttribute('apparition-delay') || '0', 10) * 1000;
  }

  get shouldAppearAutomatically() {
    return !(
      localStorage.getItem('theme:popup-filled') === 'true' || // Never open if the popup has been filled successfully
      (this.hasAttribute('only-once') && localStorage.getItem('theme:popup-appeared') === 'true')
    );
  }

  attributeChangedCallback(name, oldValue, newValue) {
    super.attributeChangedCallback(name, oldValue, newValue);

    if (name === 'open' && this.open) {
      localStorage.setItem('theme:popup-appeared', 'true');
    }
  }
}

if (!window.customElements.get('newsletter-modal-popup')) {
  window.customElements.define('newsletter-modal-popup', NewsletterModalPopup);
}
